<template>
  <Teleport to=".lower-zindex">
    <div
      :id="id"
      class="noClosePanel fixed left-0 top-0 z-[9999] box-border grid h-full w-full items-center justify-center overflow-auto pt-1.5"
      :class="{
        'bg-black/[.65]': !inverse,
        'bg-white/[.65]': inverse,
      }"
      :data-testid="dataTestid"
      @click.self="$emit('close')"
    >
      <!--PREMADE DIALOG STRUCTURE -->
      <div
        v-if="!$slots['custom-modal']"
        role="dialog"
        class="relative m-auto w-[1145px] max-w-[98vw] rounded-xl"
        :class="[
          {
            'overflow-hidden': !overflowVisible,
            '!w-[95vw]': xxl,
            '!w-[1145]': xl,
            '!w-[600px]': lg,
            '!w-[500px]': md,
            'min-h-[452px] !w-[380px]': xs && !noMinHeight,
            '!w-[380px]': xs && noMinHeight,
            'bg-gray-900 dark:bg-gray-700': !inverse,
            'bg-gray-10 shadow-[0_16px_16px_-10px_rgba(0,0,0,0.1),0_0_40px_1px_rgba(0,0,0,0.1)]':
              inverse,
          },
          width,
        ]"
      >
        <TButton
          v-if="!noClose"
          class="!absolute right-[8px] top-[8px]"
          data-testid="close-modal"
          :inverse="inverse"
          @click="$emit('close')"
        >
          <template #icon>
            <svg
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M13.9609 5.48438C14.2422 5.17969 14.7344 5.17969 15.0156 5.48438C15.3203 5.76562 15.3203 6.25781 15.0156 6.53906L11.0547 10.5L15.0156 14.4844C15.3203 14.7656 15.3203 15.2578 15.0156 15.5391C14.7344 15.8438 14.2422 15.8438 13.9609 15.5391L10 11.5781L6.01562 15.5391C5.73438 15.8438 5.24219 15.8438 4.96094 15.5391C4.65625 15.2578 4.65625 14.7656 4.96094 14.4844L8.92188 10.5L4.96094 6.53906C4.65625 6.25781 4.65625 5.76562 4.96094 5.48438C5.24219 5.17969 5.73438 5.17969 6.01562 5.48438L10 9.44531L13.9609 5.48438Z"
                fill="currentColor"
              />
            </svg>
          </template>
        </TButton>
        <div
          class="flex items-center gap-3 px-5 pb-10 pt-5"
          :class="{
            '!pb-4': headerLow,
            '!bg-gray-900': headerDark && !inverse,
            '!bg-gray-50': headerDark && inverse,
          }"
        >
          <div
            v-if="$slots.icon"
            class="box-border grid h-[40px] w-[40px] items-center justify-center rounded-md border border-solid"
            :class="{
              'border-gray-500 text-gray-50': !inverse,
              'border-gray-300 text-gray-700': inverse,
            }"
          >
            <slot name="icon" />
          </div>
          <div
            class="text-md font-regular font-inter leading-4"
            :class="{
              'text-gray-100': !inverse,
              'text-gray-700': inverse,
            }"
          >
            <h1
              class="mb-1 text-base font-semibold leading-4"
              :class="{
                'text-gray-50': !inverse,
                'text-gray-800': inverse,
              }"
            >
              {{ header }}
            </h1>
            <p class="text-sm leading-4">{{ description }}</p>
          </div>
          <div v-if="$slots['dialog-header']" class="flex-1">
            <slot name="dialog-header" />
          </div>
        </div>
        <div class="body">
          <slot name="dialog-body" />
        </div>
        <div class="footer">
          <slot name="dialog-footer" />
        </div>
      </div>

      <!-- CUSTOM MODAL e.g. FileManager -->
      <div v-else class="m-auto">
        <slot name="custom-modal" />
      </div>
    </div>
  </Teleport>
</template>

<script lang="ts" setup>
import { onMounted, onBeforeUnmount } from "vue";
import TButton from "../TButton/TButton.vue";

withDefaults(
  defineProps<{
    header?: string;
    description?: string;
    xs?: boolean;
    md?: boolean;
    lg?: boolean;
    xl?: boolean;
    xxl?: boolean;
    headerLow?: boolean;
    headerDark?: boolean;
    dark?: boolean;
    noClose?: boolean;
    dataTestid?: string;
    noMinHeight?: boolean;
    overflowVisible?: boolean;
    id?: string;
    width?: string;
    inverse?: boolean;
  }>(),
  {
    width: "",
  },
);

const emit = defineEmits(["close"]);

function closeOnEscKey(e: KeyboardEvent) {
  if (e.key === "Escape") {
    //@ts-expect-error
    if (e.target.localName !== "input") {
      emit("close");
    }
  }
}

onMounted(() => {
  document.addEventListener("keydown", closeOnEscKey);
});

onBeforeUnmount(() => {
  document.removeEventListener("keydown", closeOnEscKey);
});
</script>

<style lang="scss"></style>
